import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layers, Plus, MapPin, Eye, Sparkles, MoreVertical, Handshake, Pencil } from "lucide-react";
import { Fade, priceLabel } from "@/components/proptech";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { saleTypeShortLabel } from "@/lib/taxonomy";
import { PropertyCover } from "@/components/AdaptiveMediaFrame";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const FILTERS = ["Active", "Stale", "Under Negotiation", "Reserved", "Sold", "Leased", "Archived"];
const ACTIONS = ["Active", "Paused", "Under Negotiation", "Reserved", "Sold", "Leased", "Unavailable"];

// Reusable Properties management content (filters, list, status/archive actions).
// Rendered by the standalone /my-properties page (with its own header) and by the
// Opportunities module (embedded — its own header hidden). Business logic lives ONLY here.
export function MyPropertiesPanel({ embedded = false }) {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/me/properties" + (filter ? `?status=${encodeURIComponent(filter)}` : "")).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [filter]);

  const setStatus = async (id, status) => {
    try { await api.patch(`/properties/${id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (id) => {
    try { await api.delete(`/properties/${id}`); toast.success("Archived"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // Shared actions menu — reused by the mobile overlay (<lg) and desktop content row (>=lg).
  const renderMenu = (p, triggerClass, sfx = "") => (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid={`actions-${p.id}${sfx}`} className={triggerClass}><MoreVertical className="h-4 w-4" /></DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid={`pmatches-${p.id}${sfx}`} onClick={() => nav(`/property/${p.id}/matches`)} className="gap-2"><Sparkles className="h-4 w-4" /> View matches</DropdownMenuItem>
        <DropdownMenuItem data-testid={`edit-${p.id}${sfx}`} onClick={() => nav(`/have-property?edit=${p.id}`)} className="gap-2"><Pencil className="h-4 w-4" /> Edit details</DropdownMenuItem>
        {ACTIONS.map((a) => <DropdownMenuItem key={a} data-testid={`set-${a.replace(/\s/g, '-')}-${p.id}${sfx}`} onClick={() => setStatus(p.id, a)}>Mark {a}</DropdownMenuItem>)}
        <DropdownMenuItem onClick={() => archive(p.id)} className="text-red-600" data-testid={`archive-${p.id}${sfx}`}>Archive</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <>
      {!embedded && (
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <p className="eyebrow flex items-center gap-2"><Layers className="h-4 w-4" /> My Properties</p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Your listings</h1>
          </div>
          <Button data-testid="add-property-btn" onClick={() => nav("/have-property")} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-xl font-semibold"><Plus className="h-4 w-4 mr-1.5" /> Add Property</Button>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-5">
        <button data-testid="filter-All" onClick={() => setFilter("")} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === "" ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-slate-600"}`}>All</button>
        {FILTERS.map((s) => (
          <button key={s} data-testid={`filter-${s.replace(/\s/g, "-")}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-sm font-medium border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-slate-600"}`}>{s}</button>
        ))}
      </div>

      {items && items.length === 0 && (
        <Fade><div className="bg-slate-50 border border-dashed border-border rounded-2xl p-12 text-center max-w-xl">
          <Layers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="font-display text-lg font-semibold text-slate-800">You haven't added any properties yet.</p>
          <Button data-testid="add-first-property" onClick={() => nav("/have-property")} className="mt-4 bg-accent text-accent-foreground rounded-xl">Add your first property</Button>
        </div></Fade>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="my-properties-list">
        {items?.map((p, i) => (
          <Fade key={p.id} delay={0.04 * i}>
            <div data-testid={`property-${p.id}`} className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-md transition-shadow">
              {/* < 1024px: existing fixed-height cover with overlaid controls (unchanged) */}
              <div className="lg:hidden relative h-36">
                <PropertyCover listing={p} aspect={null} className="absolute inset-0 h-full w-full" />
                <span className="absolute top-2 left-2 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/95 text-slate-700">{p.status}</span>
                {p.listing_type === "Unit Hosting" && <span data-testid={`hosting-badge-${p.id}`} className="absolute top-2 left-2 mt-7 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-primary text-primary-foreground">Unit Hosting</span>}
                {renderMenu(p, "absolute top-2 right-2 h-8 w-8 rounded-full bg-white/95 flex items-center justify-center", "-m")}
              </div>

              {/* >= 1024px: consistent 4:3 cover (no controls over artwork) + control row below */}
              <div className="hidden lg:block">
                <PropertyCover listing={p} aspect="4/3" desktopCover />
                <div className="flex items-center gap-2 flex-wrap px-4 pt-3" data-testid={`property-controls-${p.id}`}>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    <span className={`h-1.5 w-1.5 rounded-full ${p.status === "Active" ? "bg-emerald-500" : p.status === "Archived" ? "bg-slate-400" : "bg-[#C79A3B]"}`} />{p.status}
                  </span>
                  {p.listing_type === "Unit Hosting" && <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-primary text-primary-foreground">Unit Hosting</span>}
                  {renderMenu(p, "ml-auto h-8 w-8 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 flex items-center justify-center transition-colors")}
                </div>
              </div>

              <div className="p-4">
                <h3 className="font-display font-semibold text-slate-900 truncate">{p.title}</h3>
                <div className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="h-3.5 w-3.5" />{[p.neighborhood, p.city].filter(Boolean).join(", ")}</div>
                <div className="font-display text-lg font-bold text-slate-900 mt-1" data-testid={`price-${p.id}`}>{p.listing_type === "Unit Hosting" ? `₱${(p.nightly_rate || 0).toLocaleString()} / night` : priceLabel(p)}</div>
                {p.listing_type === "Unit Hosting" && <div className="text-xs text-slate-500 mt-0.5">Up to {p.max_guests} guests · {p.availability_status?.replace(/_/g, " ").toLowerCase()}</div>}
                {p.listing_type === "For Sale" && p.sale_type && <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mt-1" data-testid={`sale-type-${p.id}`}>For Sale · {saleTypeShortLabel(p.sale_type)}</div>}
                {(p.updated_at || p.created_at) && <div className="text-[11px] text-slate-400 mt-1" data-testid={`freshness-${p.id}`}>Updated {new Date(p.updated_at || p.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</div>}
                <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border text-xs text-slate-500">
                  <span className="flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> {p.match_count} matches</span>
                  <span className="flex items-center gap-1"><Handshake className="h-3.5 w-3.5" /> {p.collaboration_count} collabs</span>
                  <Link to={`/property/${p.id}`} className="ml-auto text-accent font-semibold flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> View</Link>
                </div>
              </div>
            </div>
          </Fade>
        ))}
      </div>
    </>
  );
}
